/**
 * Billing Slice
 * Redux slice for billing management state
 */

import { createSlice } from '@reduxjs/toolkit';
import { BillingState } from '../../types/billing';
import {
  cancelSubscription,
  createBillingPortalSession,
  fetchBilling,
  fetchInvoices,
  fetchPackages,
  fetchRateLimits,
  fetchUsage,
  resumeSubscription,
  updateSubscriptionPackage,
} from './billingThunks';

const initialState: BillingState = {
  subscription: null,
  packages: [],
  invoices: [],
  usage: null,
  planRateLimits: {},
  isLoading: false,
  error: null,
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearBillingError: (state) => {
      state.error = null;
    },
    clearBilling: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch billing
    builder
      .addCase(fetchBilling.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBilling.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscription = action.payload;
      })
      .addCase(fetchBilling.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch packages
    builder
      .addCase(fetchPackages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packages = action.payload;
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch invoices
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch usage
    builder
      .addCase(fetchUsage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.usage = action.payload;
      })
      .addCase(fetchUsage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch plan rate limits
    builder.addCase(fetchRateLimits.fulfilled, (state, action) => {
      state.planRateLimits = action.payload;
    });

    // Create billing portal session
    builder
      .addCase(createBillingPortalSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBillingPortalSession.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createBillingPortalSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Cancel subscription
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.isLoading = false;
        if (state.subscription) {
          state.subscription.cancel_at_period_end = true;
        }
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Resume subscription
    builder
      .addCase(resumeSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resumeSubscription.fulfilled, (state) => {
        state.isLoading = false;
        if (state.subscription) {
          state.subscription.cancel_at_period_end = false;
        }
      })
      .addCase(resumeSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update subscription package
    builder
      .addCase(updateSubscriptionPackage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSubscriptionPackage.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateSubscriptionPackage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearBillingError, clearBilling } = billingSlice.actions;

export default billingSlice.reducer;
